// Dependencies
import React, { Component, cloneElement, PropTypes } from 'react';
import R from 'ramda';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import {
  sendCredentials,
  confirmSecondaryEmail,
  confirmDisassociateSecondary,
  clearInfoBoxMessage,
  updateInfoBox,
} from '../../stores/sign-in/sign-in-actions';
const logoGlobeSrc = require('../../../www/images/tourconnect-logo-globe.svg');
const logoNameSrc = require('../../../www/images/tourconnect-logo-name.svg');
import { browser } from 'bowser';
import debounce from 'lodash.debounce';
import languagesObj from '../../localization/languages.json';
const languageSettings = languagesObj['en-US'];
import docCookies from '../../utils/docCookie';
import { pathOr } from 'ramda';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import muiTheme from '../../utils/muiTheme';
// Components
const acceptedBrowserVersions = {
  'Internet Explorer': 11,
  Firefox: 35,
  Chrome: 39,
  Safari: 7.1,
  Opera: 27,
  'Microsoft Edge': 12,
};

class SignInContainer extends Component {

  constructor(props) {
    super(props);
    const emailInCookie = docCookies.getItem('email');
    this.state = {
      email: props.location.query.email || emailInCookie,
      password: '',
      rememberMe: Boolean(emailInCookie),
    };
  }

  componentDidMount() {
    const { reauth, auth, redirect_to, logout } = pathOr('/', ['props', 'location', 'query'], this);
    if (logout) {
      localStorage.removeItem('jwt');
      // remove the logout param
      window.history.replaceState({}, '', `${window.location.protocol}//${window.location.host}/signin`);
    } else {
      const jwt = localStorage.getItem('jwt');
      if (reauth === 'true' && jwt) {
        // something is wrong with the jwt, need to reauthenticate
        localStorage.removeItem('jwt');
      } else if (auth === 'true' && jwt && redirect_to.indexOf(process.env.NEW_SYSTEM_URL) === 0) {
        window.location = `${redirect_to}${redirect_to.indexOf('?') > -1 ? '&' : '?'}jwt=${jwt}`;
      }
    }
    const acceptedBrowserVersion = acceptedBrowserVersions[browser.name];
    let error;
    // if (browser.mobile) {
    //   error = 'TourConnect has noticed that you are on a mobile device. Please access TourConnect from a desktop to ensure the site functions properly and provides you with the best security and experience.';
    // } else
    if (!acceptedBrowserVersion) {
      error = 'TourConnect has noticed that your browser is out of date. Please update your browser to ensure the site functions properly and provides you with the best security and experience.';
    } else if (browser.version < acceptedBrowserVersion) {
      error = `TourConnect has noticed that your browser (${browser.name} ${browser.version}) is out of date. Please update your browser to ensure the site functions properly and provides you with the best security and experience.`;
    }
    if (error) {
      this._onSigninError(error);
    }
  }

  componentWillReceiveProps() {
    this.debounceDismissInfoBox(this);
  }

  onSubmit = (options = {}) => {
    this.setState(options, () => {
      this.sendCredentials();
    });
  };

  onCheckboxChanged = () => {
    const rememberMe = this.state.rememberMe;
    this.setState({ rememberMe: !rememberMe });
  };

  getLocalizedError = error => {
    switch (error) {
      case 'Please provide a valid email.':
        return languageSettings.signin.invalidEmail;
      case 'Please provide a password.':
        return languageSettings.signin.invalidPassword;
      case 'Invalid email or password.':
        return languageSettings.signin.bothInvalid;
      case 'Email not confirmed':
        return languageSettings.signin.verify;
      case 'Not approved':
        return 'You have not been approved as a user for your company, yet. We will notify you once your administrator has approved you as a user.';
      case 'Email not confirmed and not approved':
        return 'Please confirm your email address so that your administrator can add you as a user for your company.';
      case 'User not found':
        return languageSettings.signin.userNotFound;
      case 'Incorrect password':
        return languageSettings.signin.incorrectPassword;
      case 'Disabled':
        return languageSettings.signin.disabledUser;
      case 'Deleted':
        return languageSettings.signin.deletedUser;
      default:
        return languageSettings.error.general;
    }
  };

  getRouteName() {
    const pathname = this.props.location.pathname;
    if (pathname === '/signin/forgot-password') return 'forgot-password';
    else if (pathname === '/signin/disassociate-secondary') return 'disassociate-secondary';
    else if (pathname === '/signin/resend-confirmation') return 'resend-confirmation';
    else if (pathname === '/signin/resend-secondary-confirmation-email') return 'resend-secondary-confirmation-email';
    else if (pathname.indexOf('/signin/newpassword') > -1) return 'newpassword';
    else if (pathname.indexOf('/signin/email/confirmation') > -1) return 'confirmation';
    else if (pathname.indexOf('/signin/email/secondary-confirmation') > -1) return 'secondary-confirmation';
    else if (pathname.indexOf('/signin/email/confirm-disassociate') > -1) return 'confirm-disassociate';
  }

  debounceDismissInfoBox = debounce(() => {
    this.clearInfoBoxMessage();
  }, 15000);

  sendCredentials = () => {
    const { dispatch } = this.props;
    const { email, password, rememberMe } = this.state;
    if (rememberMe) {
      if (email) docCookies.setItem('email', email, undefined, '/', `${window.location.host.split('.')[1]}.com`);
    } else {
      if (docCookies.hasItem('email')) docCookies.removeItem('email', '/', `${window.location.host.split('.')[1]}.com`);
    }
    const sso = pathOr(null, ['props', 'location', 'query', 'sso'], this);
    const sig = pathOr(null, ['props', 'location', 'query', 'sig'], this);
    // console.log('query', pathOr('/', ['props', 'location', 'query'], this));
    return dispatch(sendCredentials({
      email,
      password,
      rememberMe,
      sso,
      sig,
    }))
    .then(({ user }) => {
      localStorage.setItem('jwt', user.jwt);
      const redirect = pathOr('/', ['props', 'location', 'query', 'redirect_to'], this);
      const genAiProds = [
        'bookings', 'emailAssist', 'itineraryAssist',
      ];
      const usersAiProds = R.intersection(user.allApps || [], genAiProds);
      if (user.sso_redirect_url) window.location = user.sso_redirect_url;
      else if (redirect && redirect !== '/') {
        if (redirect.indexOf(process.env.NEW_SYSTEM_URL) === 0) {
          window.location = `${redirect}${redirect.indexOf('?') > -1 ? '&' : '?'}jwt=${user.jwt}`;
        } else {
          window.history.pushState({}, '', `${window.location.protocol}//${window.location.host}`);
          window.location = `${window.location.protocol}//${window.location.host}${redirect}`;
        }
      } else if (usersAiProds.length) {
        window.location = `${process.env.NEW_SYSTEM_URL}/generative-ai/${usersAiProds[0]}?jwt=${localStorage.getItem('jwt')}`;
      } else if (user.isShellAccount) {
        if (user.companyType === 'Contractor') {
          window.location = `${process.env.NEW_SYSTEM_URL}/contracting/contract-list?jwt=${localStorage.getItem('jwt')}`;
        } else {
          window.location = `${window.location.protocol}//${window.location.host}/contracting/contract-list/all%20contracts`;
        }
      } else window.location = `${window.location.protocol}//${window.location.host}/`;
    }).catch((err) => {
      this._onSigninError(this.getLocalizedError(err.message));
      this.debounceDismissInfoBox();
    });
  };

  _onSigninError = (error) => {
    this.setState({ sending: false });
    this.updateInfoBox({ signinError: error });
  };

  updateInfoBox = (messages = {}) => {
    const { dispatch } = this.props;
    dispatch(updateInfoBox(messages));
  };

  clearInfoBoxMessage = () => {
    const { dispatch } = this.props;
    dispatch(clearInfoBoxMessage());
  };

  confirmSigninEmail = token => {
    console.log('hit here');
    return this.props.dispatch({ type: 'CONFIRM_SIGNIN_EMAIL', payload: { token } });
  }
  confirmSecondaryEmail = (token) => {
    const { dispatch } = this.props;
    return dispatch(confirmSecondaryEmail(token));
  };

  confirmDisassociateSecondary = (token) => {
    const { dispatch } = this.props;
    return dispatch(confirmDisassociateSecondary(token));
  };

  render() {
    const { auth, reauth, redirect_to = '' } = pathOr({}, ['props', 'location', 'query'], this);
    const jwt = localStorage.getItem('jwt');
    if (auth === 'true' && reauth !== 'true' && jwt && redirect_to.indexOf(process.env.NEW_SYSTEM_URL) === 0) {
      return (
        <div>
          Redirecting...
        </div>
      );
    }
    const { children, ...props } = this.props;
    const extraProps = {
      sendCredentials: this.sendCredentials,
      confirmSecondaryEmail: this.confirmSecondaryEmail,
      confirmSigninEmail: this.confirmSigninEmail,
      confirmDisassociateSecondary: this.confirmDisassociateSecondary,
      onSubmit: this.onSubmit,
      ...this.state,
      signinError: this.props.signin.signinError,
      signinInfo: this.props.signin.signinInfo,
      onCheckboxChanged: this.onCheckboxChanged,
      routeName: this.getRouteName(),
      debounceDismissInfoBox: this.debounceDismissInfoBox,
      clearInfoBoxMessage: this.clearInfoBoxMessage,
      updateInfoBox: this.updateInfoBox,
      ...props,
    };
    return (
      <MuiThemeProvider muiTheme={muiTheme}>
        <div>
          <div className="app-bar is-not-logged-in">
            <div className="app-bar-content">
              <Link className="tourconnect-logo" to="/">
                <img
                  className="tourconnect-logo-globe"
                  src={logoGlobeSrc}
                />
                <img
                  className="tourconnect-logo-name"
                  src={logoNameSrc}
                  alt="tourconnect"
                />
              </Link>
            </div>
          </div>
          {children ? cloneElement(children, extraProps) : undefined}
        </div>
      </MuiThemeProvider>
    );
  }

}

SignInContainer.propTypes = {
  location: PropTypes.object,
  dispatch: PropTypes.func,
  children: PropTypes.object,
  signin: PropTypes.object,
  confirmSigninEmail: PropTypes.func,
};

const select = (state) => {
  return {
    signin: state.signin.toJS(),
    route: state.route,
  };
};

export default connect(select)(SignInContainer);



// WEBPACK FOOTER //
// ./src/app/components/signin/signin-container.jsx